'use client'

// SUMMARY: A single chat row — user bubble or assistant markdown with copy action and streaming indicator

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { Check, Copy, FileText, Image as ImageIcon, Loader2, Search, Sparkles } from 'lucide-react'
import { parseMessageContent } from '@/lib/ai/message-format'
import { stripChoiceResult } from '@/lib/ai/choice-form'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ChatMarkdown } from './chat-markdown'
import type { ChatMessage as ChatMessageType } from './use-chat'

/** User bubble: render attachments as chips and show only the typed text, not the raw context */
function UserContent({ content }: { content: string }) {
	const { docs, images, text } = parseMessageContent(content)
	// A choice card posts a hidden result block with its readable line - never show the machine block.
	const displayText = stripChoiceResult(text)
	const chips = [
		...images.map((img) => ({ name: img.name, image: true })),
		...docs.map((doc) => ({ name: doc.name, image: false })),
	]

	return (
		<div className="space-y-2">
			{chips.length > 0 && (
				<div className="flex flex-wrap gap-1.5">
					{chips.map((c, i) => (
						<span key={i} className="flex items-center gap-1 rounded-md bg-white/15 px-2 py-1 text-xs">
							{c.image ? <ImageIcon className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
							<span className="max-w-[12rem] truncate">{c.name}</span>
						</span>
					))}
				</div>
			)}
			{displayText && <div className="whitespace-pre-wrap">{displayText}</div>}
		</div>
	)
}

export function ChatMessage({ message }: { message: ChatMessageType }) {
	const t = useTranslations('dashboard.chat')
	const [copied, setCopied] = useState(false)
	const isUser = message.role === 'user'

	const copy = async () => {
		await navigator.clipboard.writeText(message.content)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}

	// No role avatars: the colour and side already say who is speaking, and dropping them lets the bubbles
	// run to the full width of the reading column instead of losing 44px to an icon gutter.
	return (
		<div className={`flex ${isUser ? 'justify-end' : ''}`}>
			<div
				className={`group flex min-w-0 flex-col gap-1 ${isUser ? 'max-w-[85%] items-end' : 'w-full max-w-full items-start'}`}
			>
				{/* Only the user speaks in a bubble. The assistant's turn is the page's own content - it carries
				    headings, tables and code blocks that a tinted box only boxes in - so it runs plain on the
				    background, flush with the reading column. */}
				<div
					className={`break-words text-sm leading-relaxed ${
						isUser ? 'rounded-2xl rounded-tr-sm bg-blue-600 px-5 py-4 text-white' : 'w-full text-foreground'
					}`}
				>
					{isUser ? (
						<UserContent content={message.content} />
					) : message.content ? (
						<ChatMarkdown content={message.content} />
					) : message.status ? (
						<StatusLine status={message.status} kind={message.statusKind} />
					) : message.streaming ? (
						<StreamingDots />
					) : null}
				</div>

				{/* Always visible - hover-only hid the one action the answer has, and on touch there is no hover */}
				{!isUser && message.content && !message.streaming && (
					<Tooltip>
						<TooltipTrigger
							onClick={copy}
							aria-label={t(copied ? 'copied' : 'copy')}
							className="-ml-1.5 mt-1 flex cursor-pointer items-center gap-1 rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
						>
							{copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
						</TooltipTrigger>
						<TooltipContent>{t(copied ? 'copied' : 'copy')}</TooltipContent>
					</Tooltip>
				)}
			</div>
		</div>
	)
}

function StatusLine({ status, kind }: { status: string; kind?: string }) {
	const t = useTranslations('dashboard.chat')
	// Document/project/research turns send a full localized progress sentence; web search sends a bare query.
	// 'thinking' is the immediate activity signal every turn opens with (replaced by any later real status).
	const isThinking = kind === 'thinking'
	const isSentence = isThinking || kind === 'document' || kind === 'project' || kind === 'research'
	const Icon = isThinking ? Sparkles : isSentence ? FileText : Search
	return (
		<span className="flex items-center gap-2 text-muted-foreground">
			<Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
			<Icon className="h-3.5 w-3.5 shrink-0" />
			<span className="truncate">
				{isSentence ? status || '…' : `${t('searching')}${status ? `: ${status}` : '…'}`}
			</span>
		</span>
	)
}

function StreamingDots() {
	return (
		<span className="flex items-center gap-1 py-0.5">
			<span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:0ms]" />
			<span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:150ms]" />
			<span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:300ms]" />
		</span>
	)
}
